import { keepPreviousData } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { useAppQuery } from '../use-app-query.hook';
import { useSelectedCompany } from '../use-selected-company.hook';
import { AccessQuery, DataRoomAccessPage } from './space.types';

export const useDataRoomAccess = (id: string | undefined, query: AccessQuery = {}) => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id ?? '';

  const { data, isError, isFetched, isLoading } = useAppQuery<DataRoomAccessPage>({
    enabled: Boolean(companyId && id),
    /**
     * A filter or a page is a new query key, and without this the list has no data for a moment:
     * the table comes off the screen, the modal collapses to a spinner and springs back once the
     * answer lands. Holding the previous page until the next one arrives keeps the rows in place
     * and the dialog the size it was.
     */
    placeholderData: keepPreviousData,
    queryFn: () =>
      ApiService.get<DataRoomAccessPage>(
        { endpoint: BackendRoute.DATA_ROOMS, routePath: [id ?? '', 'access'] },
        { queryParams: { ...query } },
      ),
    queryKey: [QueryKey.DATA_ROOM_ACCESS, companyId, id, { ...query }],
  });

  return {
    count: data?.count ?? 0,
    isEditable: data?.isEditable ?? false,
    isError,
    isFetched,
    isLoading,
    records: data?.records ?? [],
    roomStatus: data?.roomStatus,
    totalPages: data?.totalPages ?? 0,
  };
};
